import { useState, useRef } from 'react'
import { motion } from 'framer-motion';
import {Thermometer, Snowflake, Wrench, Shield, CheckCircle, Clock, Phone, Users, Award, ArrowRight} from 'lucide-react'

const Services = () => {  
  const [selectedService, setSelectedService] = useState(0)
  const serviceDetailRef = useRef<HTMLDivElement>(null)
  const serviceTabsRef = useRef<HTMLDivElement>(null)

  const services = [
    {
      id: 'ar-condicionado',
      icon: Thermometer,
      title: 'Ar Condicionado Comercial e Industrial',
      description: 'Instalação, manutenção e reparo de sistemas de ar condicionado para empresas e lojas',
      image: 'https://images.pexels.com/photos/6758773/pexels-photo-6758773.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Instalação profissional certificada',
        'Manutenção preventiva e corretiva',
        'Sistemas split, multi-split e VRV',
        'Garantia de 6 meses em instalações',
        'Atendimento 24/7 para contratos comerciais',
        'Técnicos especializados e certificados'
      ],
      benefits: [
        'Economia de até 30% no consumo de energia',
        'Maior durabilidade dos equipamentos',
        'Qualidade do ar melhorada',
        'Controle preciso de temperatura'
      ]
    },
    {
      id: 'refrigeracao-industrial',
      icon: Snowflake,
      title: 'Refrigeração Industrial',
      description: 'Soluções completas para refrigeração industrial, câmaras frias e sistemas de grande porte',
      image: 'https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Câmaras frigoríficas sob medida',
        'Sistemas de refrigeração industrial',
        'Controle automático de temperatura',
        'Monitoramento remoto 24/7',
        'Gases ecológicos (R-404A, R-507)',
        'Projetos personalizados'
      ],
      benefits: [
        'Redução de perdas de produtos',
        'Controle rigoroso de temperatura',
        'Eficiência energética máxima',
        'Conformidade com normas sanitárias'
      ]
    },
    {
      id: 'manutencao',
      icon: Wrench,
      title: 'Manutenção Preventiva e Corretiva',
      description: 'Serviços especializados de manutenção para garantir o funcionamento perfeito dos seus equipamentos',
      image: 'https://images.pexels.com/photos/5691621/pexels-photo-5691621.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Planos de manutenção personalizados',
        'Limpeza e higienização completa',
        'Troca de filtros e componentes',
        'Verificação de gás refrigerante',
        'Relatórios técnicos detalhados',
        'Agendamento flexível'
      ],
      benefits: [
        'Aumento da vida útil dos equipamentos',
        'Prevenção de defeitos e quebras',
        'Manutenção da garantia',
        'Economia em reparos futuros'
      ]
    },
    {
      id: 'projetos-personalizados',
      icon: Shield,
      title: 'Projetos Personalizados',
      description: 'Desenvolvimento de soluções sob medida para necessidades específicas de climatização',
      image: 'https://images.pexels.com/photos/3862132/pexels-photo-3862132.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Análise técnica detalhada',
        'Projeto 3D e simulações',
        'Cálculo de carga térmica',
        'Seleção de equipamentos ideais',
        'Acompanhamento pós-instalação',
        'Suporte técnico especializado'
      ],
      benefits: [
        'Solução perfeitamente adequada',
        'Máxima eficiência energética',
        'ROI otimizado',
        'Tecnologia de ponta'
      ]
    }
  ]

  const process = [
    {
      step: '01',
      title: 'Consulta Inicial',
      description: 'Análise das suas necessidades e visita técnica gratuita'
    },
    {
      step: '02',
      title: 'Projeto e Orçamento',
      description: 'Desenvolvimento da solução ideal e apresentação de proposta'
    },
    {
      step: '03',
      title: 'Execução',
      description: 'Instalação profissional com equipe especializada'
    },
    {
      step: '04',
      title: 'Emergência 24/7',
      description: 'Contratos comerciais'
    }
  ]

  // Gerenciar mudança de serviço e anunciar para leitores de tela
  const handleServiceChange = (index: number) => {
    setSelectedService(index);
    
    // Anunciar mudança para leitores de tela
    const serviceName = services[index].title;
    announceToScreenReader(`Serviço selecionado: ${serviceName}`);
    
    // Fazer scroll suave para a seção de detalhes se estiver em mobile
    setTimeout(() => {
      if (window.innerWidth < 1024 && serviceDetailRef.current) {
        serviceDetailRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  // Função para anunciar informações aos leitores de tela
  const announceToScreenReader = (message: string) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  // Controle de teclado para navegação entre serviços
  const handleServiceKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case 'ArrowLeft': {
        e.preventDefault();
        const prevIndex = index === 0 ? services.length - 1 : index - 1;
        handleServiceChange(prevIndex);
        // Focar no serviço anterior
        setTimeout(() => {
          const prevButton = serviceTabsRef.current?.children[prevIndex] as HTMLButtonElement;
          prevButton?.focus();
        }, 100);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        const nextIndex = index === services.length - 1 ? 0 : index + 1;
        handleServiceChange(nextIndex);
        // Focar no próximo serviço
        setTimeout(() => {
          const nextButton = serviceTabsRef.current?.children[nextIndex] as HTMLButtonElement;
          nextButton?.focus();
        }, 100);
        break;
      }
      case 'Home':
        { // Adicionado bloco para escopo lexical
          e.preventDefault();
          handleServiceChange(0);
          setTimeout(() => {
            const firstButton = serviceTabsRef.current?.children[0] as HTMLButtonElement;
            firstButton?.focus();
          }, 100);
          break;
        } // Fechamento do bloco
      case 'End':
        { // Adicionado bloco para escopo lexical
          e.preventDefault();
          handleServiceChange(services.length - 1);
          setTimeout(() => {
            const lastButton = serviceTabsRef.current?.children[services.length - 1] as HTMLButtonElement;
            lastButton?.focus();
          }, 100);
          break;
        } // Fechamento do bloco
    }
  };

  return (
    <div className="pt-20">
      {/* Skip to main content link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded-lg z-50 focus:z-50"
      >
        Pular para o conteúdo principal
      </a>

      {/* Hero Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white"
        aria-labelledby="services-hero-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 id="services-hero-title" className="text-5xl md:text-6xl font-bold mb-6">
              Nossos Serviços
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Soluções completas em refrigeração e climatização com 
              tecnologia de ponta e qualidade garantida
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section 
        id="main-content"
        className="py-20 bg-white"
        aria-labelledby="services-overview-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="services-overview-title" className="sr-only">
            Visão geral dos nossos serviços
          </h2>

          {/* Service Tabs */}
          <div 
            ref={serviceTabsRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
            role="tablist"
            aria-label="Navegação entre serviços"
          >
            <div className="sr-only" id="service-tabs-instructions">
              Use as setas esquerda e direita para navegar entre os serviços. 
              Home vai para o primeiro, End para o último.
            </div>

            {services.map((service, index) => (
              <motion.button
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleServiceChange(index)}
                onKeyDown={(e) => handleServiceKeyDown(e, index)}
                className={`p-6 rounded-xl transition-all duration-300 text-left focus:ring-4 focus:ring-blue-300 focus:outline-none ${
                  selectedService === index 
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-xl' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                role="tab"
                aria-selected={selectedService === index}
                aria-controls={`service-panel-${service.id}`}
                aria-describedby="service-tabs-instructions"
                id={`service-tab-${service.id}`}
              >
                <service.icon 
                  className={`h-12 w-12 mb-4 ${
                    selectedService === index ? 'text-white' : 'text-blue-600'
                  }`} 
                  aria-hidden="true"
                />
                <h3 className="text-lg font-semibold mb-2">
                  {service.title}
                </h3>
                <p className={`text-sm ${
                  selectedService === index ? 'text-blue-100' : 'text-gray-600'
                }`}>
                  {service.description}
                </p>
              </motion.button>
            ))}
          </div>

          {/* Service Detail */}
          <motion.div
            key={selectedService}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            role="tabpanel"
            aria-labelledby={`service-tab-${services[selectedService].id}`}
            id={`service-panel-${services[selectedService].id}`}
            ref={serviceDetailRef}
          >
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {services[selectedService].title}
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                {services[selectedService].description}
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Características do Serviço:
                </h3>
                <ul className="space-y-3" role="list">
                  {services[selectedService].features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircle 
                        className="h-5 w-5 text-green-500 flex-shrink-0" 
                        aria-hidden="true"
                      />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Benefícios:
                </h3>
                <ul className="space-y-3" role="list">
                  {services[selectedService].benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <ArrowRight 
                        className="h-5 w-5 text-blue-600 flex-shrink-0" 
                        aria-hidden="true"
                      />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative">
              <img
                src={services[selectedService].image}
                alt={`Imagem ilustrativa do serviço: ${services[selectedService].title}`}
                className="rounded-lg shadow-xl w-full h-auto"
                loading="lazy"
              />
              <div 
                className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-500/20 rounded-lg"
                aria-hidden="true"
              ></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section 
        className="py-20 bg-gray-50"
        aria-labelledby="process-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="process-title" className="text-4xl font-bold text-gray-900 mb-4">
              Nosso Processo de Trabalho
            </h2>
            <p className="text-xl text-gray-600">
              Metodologia comprovada para garantir os melhores resultados
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div 
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold"
                  aria-label={`Etapa ${step.step}`}
                >
                  <span aria-hidden="true">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Processo em formato de lista para leitores de tela */}
          <div className="sr-only">
            <h3>Processo detalhado:</h3>
            <ol>
              {process.map((step, index) => (
                <li key={index}>
                  Etapa {step.step}: {step.title} - {step.description}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section 
        className="py-20 bg-white"
        aria-labelledby="why-choose-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="why-choose-title" className="text-4xl font-bold text-gray-900 mb-4">
              Por Que Escolher a Tech Glacial?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'Mais de 5 Anos de Experiência',
                description: 'Expertise comprovada em milhares de projetos realizados'
              },
              {
                icon: Users,
                title: 'Equipe Especializada',
                description: 'Técnicos certificados e em constante atualização'
              },
              {
                icon: Clock,
                title: 'Atendimento 24/7',
                description: 'Disponível para contrato'
              },
              {
                icon: Shield,
                title: 'Garantia Estendida',
                description: 'Até 6 meses de garantia em nossos serviços'
              },
              {
                icon: CheckCircle,
                title: 'Qualidade Certificada',
                description: 'Certificações de qualidade'
              },
              {
                icon: Phone,
                title: 'Orçamento Gratuito',
                description: 'Visita técnica e orçamento sem compromisso'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow duration-300"
              >
                <div 
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  aria-hidden="true"
                >
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Informações adicionais para SEO e acessibilidade */}
      <div className="sr-only">
        <h2>Informações detalhadas sobre nossos serviços</h2>
        <p>
          A Tech Glacial oferece serviços completos de refrigeração e climatização, 
          incluindo instalação, manutenção e projetos personalizados. Atendemos desde 
          residências até grandes complexos industriais com equipe técnica especializada 
          e certificada.
        </p>
        <p>
          Nossos serviços incluem: sistemas de ar condicionado comercial e residencial, 
          refrigeração industrial para câmaras frias e frigoríficos, manutenção preventiva 
          e corretiva, e desenvolvimento de projetos personalizados sob medida.
        </p>
        <p>
          Todos os serviços são executados seguindo rigorosamente as normas técnicas 
          e de segurança, com garantia estendida e suporte técnico 24/7 para contratos comerciais.
        </p>
      </div>
    </div>
  )
}

export default Services